import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SHIFT_LABELS, COST_ADDER_TYPES } from '@aisolution/shared';
import { api, type Costing as CostingData, type TravelRow, type MarkupRow, type RepriceSummary } from '../api/client';
import { parsePriceSheet } from '../lib/prices';

const money = (n: number | null | undefined) => n == null ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const WAGE_TYPES = [
  { v: 'open', label: 'Open shop' }, { v: 'ca_prevailing', label: 'CA prevailing (DIR)' },
  { v: 'nv_prevailing', label: 'NV prevailing' }, { v: 'davis_bacon', label: 'Davis-Bacon (federal)' },
];

// Phase 6 — pricing, labor/wage & costs. Internal-only estimate inputs that feed
// the sell-price preview. Proposals never show this breakdown.
export function Costing() {
  const { id = '' } = useParams();
  const [cost, setCost] = useState<CostingData | null>(null);
  const [travel, setTravel] = useState<TravelRow[]>([]);
  const [markups, setMarkups] = useState<MarkupRow[]>([]);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [reprice, setReprice] = useState<RepriceSummary | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function load() {
    try {
      const [c, t, m] = await Promise.all([api.getCosting(id), api.getTravel(id), api.getMarkups(id)]);
      setCost(c); setTravel(t.travel); setMarkups(m.markups);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load'); }
  }
  useEffect(() => { void load(); }, [id]);

  async function saveWage(patch: Record<string, string>) {
    try { await api.saveWageSettings(id, patch); await load(); } catch (e) { setError(`${e}`); }
  }
  async function doReprice() {
    setError(''); setMsg('Repricing from the hierarchy…');
    try { const r = await api.repriceBom(id); setReprice(r.summary); await load(); setMsg(''); }
    catch (e) { setError(e instanceof Error ? e.message : 'Reprice failed'); setMsg(''); }
  }
  async function onPriceFile(file: File) {
    setError(''); setMsg(`Reading ${file.name}…`);
    try {
      const rows = await parsePriceSheet(file);
      const res = await api.uploadPrices(id, rows);
      setMsg(`Imported ${res.inserted} prices${res.skipped ? `, skipped ${res.skipped}` : ''}. Click Reprice to apply.`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Price import failed'); setMsg(''); }
    finally { if (fileRef.current) fileRef.current.value = ''; }
  }

  const c = cost;
  return (
    <div>
      <Link to={`/projects/${id}/bom`} className="muted" style={{ fontSize: 13 }}>← Internal BOM</Link>
      <h1 style={{ margin: '4px 0 0' }}>Pricing, labor & costs</h1>
      <div className="banner" style={{ marginTop: 8 }}>
        <strong>Internal only.</strong> Wage rates are SAMPLE placeholders — verify against the live CA DIR / DOL
        determination. Material defaults to internet-estimated until distributor pricing is uploaded.
      </div>
      {error && <div className="error">{error}</div>}
      {msg && <div className="banner">{msg}</div>}

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
        {/* Wage settings */}
        <div className="card">
          <h2>Labor & wage basis</h2>
          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Labeled label="Wage type">
              <select value={c?.wage.type ?? 'open'} onChange={(e) => saveWage({ wage_type: e.target.value })}>
                {WAGE_TYPES.map((w) => <option key={w.v} value={w.v}>{w.label}</option>)}
              </select>
            </Labeled>
            <Labeled label="Shift">
              <select value={c?.wage.shift ?? 'straight'} onChange={(e) => saveWage({ shift: e.target.value })}>
                {Object.entries(SHIFT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Labeled>
            <Labeled label="State"><input defaultValue={c?.wage.state ?? ''} onBlur={(e) => saveWage({ state: e.target.value })} placeholder="CA / NV / ID" /></Labeled>
            <Labeled label="County"><input defaultValue={c?.wage.county ?? ''} onBlur={(e) => saveWage({ county: e.target.value })} placeholder="e.g. Clark" /></Labeled>
          </div>
          {c && c.by_category.length > 0 && (
            <table style={{ marginTop: 10 }}>
              <thead><tr><th>Classification</th><th>Hours</th><th>Rate</th><th>Labor cost</th></tr></thead>
              <tbody>
                {c.by_category.map((b) => (
                  <tr key={b.category}>
                    <td>{b.classification}{b.is_sample && <span className="pill pill-needs_review" style={{ marginLeft: 6 }}>sample</span>}</td>
                    <td>{b.hours.toFixed(2)}</td><td>{money(b.rate)}/hr</td><td>{money(b.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pricing */}
        <div className="card">
          <h2>Material pricing</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            Hierarchy: approved quote → distributor → admin DB → internet → historical. Upload a distributor
            price list or supplier quote, then reprice.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-primary btn-sm" onClick={doReprice}>Reprice BOM</button>
            <button className="btn btn-ghost btn-sm" onClick={() => fileRef.current?.click()}>Upload price list (.xlsx)</button>
            <input ref={fileRef} type="file" accept=".xlsx" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void onPriceFile(f); }} />
          </div>
          {reprice && (
            <p className="muted" style={{ marginBottom: 0 }}>
              Repriced {reprice.repriced} line(s) off internet; {reprice.still_internet} still internet-estimated
              {reprice.stale ? `; ${reprice.stale} stale` : ''}.
            </p>
          )}
        </div>
      </div>

      {/* Travel & misc */}
      <TravelCard projectId={id} travel={travel} onChange={load} />

      {/* Markups */}
      <MarkupCard projectId={id} markups={markups} onChange={load} />

      {/* Costing summary */}
      {c && (
        <div className="card" style={{ marginTop: 16 }}>
          <h2>Cost summary <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>— sell-price preview (the estimate is finalized in Phase 7)</span></h2>
          {c.warnings.map((w, i) => <div key={i} className="error">{w}</div>)}
          <table>
            <tbody>
              <Row label="Material cost" v={money(c.material_cost)} />
              <Row label="Material markup" v={money(c.material_markup)} />
              <Row label={`Labor (${c.labor_hours.toFixed(1)} hrs)`} v={money(c.labor_cost)} />
              <Row label="Travel & misc" v={money(c.travel_total)} />
              <Row label="Direct cost" v={money(c.direct_cost)} strong />
              <Row label="Overhead" v={money(c.overhead)} />
              <Row label="Profit" v={money(c.profit)} />
              <Row label="Sales tax" v={money(c.sales_tax)} />
              <Row label="Freight" v={money(c.freight)} />
              <Row label="Small tools" v={money(c.small_tools)} />
              <Row label="Contingency" v={money(c.contingency)} />
              <Row label="Bond" v={money(c.bond)} />
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, padding: '12px 14px', background: 'var(--teal-light)', borderRadius: 8 }}>
            <strong style={{ fontSize: 16 }}>Sell price (preview)</strong>
            <strong style={{ fontSize: 20, color: 'var(--teal-dark)' }}>{money(c.sell_price)}</strong>
          </div>
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <Link className="btn btn-primary btn-sm" to={`/projects/${id}/outputs`}>Outputs: RFIs, PDFs, estimate →</Link>
          </div>
        </div>
      )}
    </div>
  );
}

function TravelCard({ projectId, travel, onChange }: { projectId: string; travel: TravelRow[]; onChange: () => void }) {
  const [type, setType] = useState(COST_ADDER_TYPES[0]!.type);
  const [qty, setQty] = useState('1');
  const [unit, setUnit] = useState('');
  const [notes, setNotes] = useState('');
  async function add() {
    if (!Number(unit)) return;
    await api.addTravel(projectId, { type, qty: Number(qty) || 0, unit_cost: Number(unit) || 0, notes });
    setQty('1'); setUnit(''); setNotes(''); onChange();
  }
  const total = travel.reduce((a, t) => a + (t.qty ?? 0) * (t.unit_cost ?? 0), 0);
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h2>Travel & misc costs</h2>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <select value={type} onChange={(e) => setType(e.target.value)}>
          {COST_ADDER_TYPES.map((t) => <option key={t.type} value={t.type}>{t.label} ({t.unit})</option>)}
        </select>
        <input style={{ width: 70 }} value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Qty" />
        <input style={{ width: 100 }} value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="Unit $" />
        <input style={{ flex: 1, minWidth: 120 }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" />
        <button className="btn btn-ghost btn-sm" onClick={add}>Add</button>
      </div>
      {travel.length > 0 && (
        <table style={{ marginTop: 10 }}>
          <thead><tr><th>Type</th><th>Qty</th><th>Unit $</th><th>Ext</th><th>Notes</th><th></th></tr></thead>
          <tbody>
            {travel.map((t) => (
              <tr key={t.id}>
                <td>{COST_ADDER_TYPES.find((x) => x.type === t.type)?.label ?? t.type}</td>
                <td>{t.qty}</td><td>{money(t.unit_cost)}</td><td>{money((t.qty ?? 0) * (t.unit_cost ?? 0))}</td>
                <td className="muted">{t.notes}</td>
                <td><button className="btn btn-ghost btn-sm" onClick={async () => { await api.deleteTravel(projectId, t.id); onChange(); }}>✕</button></td>
              </tr>
            ))}
            <tr><td colSpan={3}><strong>Total</strong></td><td><strong>{money(total)}</strong></td><td colSpan={2} /></tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

function MarkupCard({ projectId, markups, onChange }: { projectId: string; markups: MarkupRow[]; onChange: () => void }) {
  const [edit, setEdit] = useState<Record<string, string>>({});
  const val = (m: MarkupRow) => edit[m.name] ?? String(m.pct_or_amount);
  async function save() {
    const rows = markups.map((m) => ({ name: m.name, pct: Number(edit[m.name] ?? m.pct_or_amount) }));
    await api.saveMarkups(projectId, rows); setEdit({}); onChange();
  }
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Markups</h2>
        <button className="btn btn-ghost btn-sm" onClick={save}>Save markups</button>
      </div>
      <table style={{ marginTop: 10 }}>
        <thead><tr><th>Markup</th><th>%</th><th>Basis</th></tr></thead>
        <tbody>
          {markups.map((m) => (
            <tr key={m.name}>
              <td>{m.name.replace(/_/g, ' ')}</td>
              <td><input style={{ width: 80 }} value={val(m)} onChange={(e) => setEdit((s) => ({ ...s, [m.name]: e.target.value }))} /></td>
              <td className="muted">{m.basis}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600 }}>{label}{children}</label>;
}
function Row({ label, v, strong }: { label: string; v: string; strong?: boolean }) {
  return <tr><td style={strong ? { fontWeight: 700 } : undefined}>{label}</td><td style={{ textAlign: 'right', ...(strong ? { fontWeight: 700 } : {}) }}>{v}</td></tr>;
}
